import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import { employeeMenuItems } from '../components/employee/employeeMenu';
import { managerMenuItems } from '../components/manager/managerMenu';
import LoadingDNA from '../components/LoadingDNA';
import { getTrips, isApiError } from '../services/dashboardService';
import {
  getExpenseEvents,
  getReimbursements,
  isExpenseApiError,
} from '../services/expenseService';

type ExpenseView = {
  id: string;
  merchant: string;
  category: string;
  amount: number;
  status: string;
  date: string;
};

type TripView = {
  id: string;
  destination: string;
  period: string;
  budgetUsed: number;
  budgetCap: number;
  status: string;
};

type ShareTimelineRow = {
  id: string;
  tripId: string;
  category: string;
  amount: number;
  status: string;
  timeline: string;
};

const filterTabs = ['All', 'Pending', 'Approved', 'Draft'] as const;
type FilterTab = (typeof filterTabs)[number];

const formatINR = (amount: number): string => `₹${amount.toLocaleString('en-IN')}`;

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeStatus = (raw?: string): 'Pending' | 'Approved' | 'Draft' | 'Rejected' => {
  const value = String(raw || '').toLowerCase();
  if (value === 'approved' || value === 'auto_approved') return 'Approved';
  if (value === 'pending') return 'Pending';
  if (value === 'draft') return 'Draft';
  if (['reimbursed', 'manager_approved', 'finance_approved'].includes(value)) return 'Approved';
  if (['pending_payment', 'payment_confirmed'].includes(value)) return 'Pending';
  return value === 'rejected' ? 'Rejected' : 'Pending';
};

const EmployeeDashboardPage: React.FC = () => {
  const location = useLocation();
  const isManagerView = location.pathname.startsWith('/manager/');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>('All');
  const [expenses, setExpenses] = useState<ExpenseView[]>([]);
  const [activeTrip, setActiveTrip] = useState<TripView | null>(null);
  const [myShares, setMyShares] = useState<ShareTimelineRow[]>([]);
  const [payerSummary, setPayerSummary] = useState({
    paid: 0,
    approvedReimbursable: 0,
    atRisk: 0,
  });

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        const [tripRows, events, reimbursements] = await Promise.all([
          getTrips({ perPage: 300 }),
          getExpenseEvents({ perPage: 300 }),
          getReimbursements({ perPage: 300 }),
        ]);
        const splitRows = events.flatMap((event) =>
          (event.splits || []).map((split) => ({
            id: split.id,
            trip_id: event.trip_id,
            merchant: event.merchant || '-',
            category: event.category || 'Expense',
            amount: Number(split.split_amount || 0),
            status: String(split.status || ''),
            created_at: split.created_at || event.created_at,
          })),
        );

        const expenseViews: ExpenseView[] = splitRows
          .sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())
          .map((item) => ({
            id: item.id,
            merchant: item.merchant || '-',
            category: item.category || 'Expense',
            amount: Number(item.amount || 0),
            status: normalizeStatus(item.status),
            date: formatDate(item.created_at),
          }));

        setExpenses(expenseViews);

        const shares: ShareTimelineRow[] = events
          .flatMap((event) =>
            (event.splits || []).map((split) => {
              const approvals = (event.approvals || [])
                .filter((approval) => approval.expense_split_id === split.id)
                .map((approval) => `${approval.approver_type}: ${approval.decision}`)
                .join(' → ');
              return {
                id: split.id,
                tripId: event.trip_id,
                category: event.category || 'Expense',
                amount: Number(split.split_amount || 0),
                status: normalizeStatus(split.status),
                timeline: approvals || 'Awaiting decision',
              };
            }),
          )
          .slice(0, 8);
        setMyShares(shares);

        const paid = reimbursements.reduce((sum, row) => sum + Number(row.reimbursable_amount || 0) + Number(row.rejected_risk_amount || 0), 0);
        const approvedReimbursable = reimbursements.reduce((sum, row) => sum + Number(row.reimbursable_amount || 0), 0);
        const atRisk = reimbursements.reduce((sum, row) => sum + Number(row.rejected_risk_amount || 0), 0);
        setPayerSummary({ paid, approvedReimbursable, atRisk });

        const activeCandidate = tripRows.find((trip) =>
          ['draft', 'pending', 'approved'].includes(String(trip.status || '')))
          || tripRows[0];
        if (activeCandidate) {
          const tripExpenseAmount = splitRows
            .filter((expense) => expense.trip_id === activeCandidate.id)
            .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
          const declaredBudget = Number(activeCandidate.estimated_budget || 0);
          const budgetCap = declaredBudget > 0 ? declaredBudget : Math.max(tripExpenseAmount, 1);
          setActiveTrip({
            id: activeCandidate.id,
            destination: activeCandidate.destination || 'Business Trip',
            period: activeCandidate.start_date && activeCandidate.end_date
              ? `${formatDate(activeCandidate.start_date)} - ${formatDate(activeCandidate.end_date)}`
              : formatDate(activeCandidate.start_date || activeCandidate.created_at),
            budgetUsed: tripExpenseAmount,
            budgetCap,
            status: String(activeCandidate.status || 'pending'),
          });
        } else {
          setActiveTrip(null);
        }
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load employee dashboard.');
      } finally {
        setIsLoading(false);
      }
    };
    void load();
  }, []);

  const filteredExpenses = useMemo(() => {
    if (filter === 'All') return expenses;
    return expenses.filter((item) => item.status === filter);
  }, [expenses, filter]);

  const spendingByCategory = useMemo(() => {
    const totals = new Map<string, number>();
    expenses.forEach((item) => {
      totals.set(item.category, (totals.get(item.category) || 0) + item.amount);
    });
    const all = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    const totalAmount = all.reduce((sum, [, value]) => sum + value, 0);
    const topThree = all.slice(0, 3).map(([name, value]) => ({
      name,
      value,
      percent: totalAmount > 0 ? Math.round((value / totalAmount) * 100) : 0,
    }));
    return { totalAmount, topThree };
  }, [expenses]);

  const budgetPercent = useMemo(() => {
    if (!activeTrip || activeTrip.budgetCap <= 0) return 0;
    return Math.min(100, Math.round((activeTrip.budgetUsed / activeTrip.budgetCap) * 100));
  }, [activeTrip]);
  const remainingBudget = Math.round((activeTrip?.budgetCap || 0) - (activeTrip?.budgetUsed || 0));

  const approvedCount = expenses.filter((item) => item.status === 'Approved').length;
  const pendingCount = expenses.filter((item) => item.status === 'Pending').length;
  const draftCount = expenses.filter((item) => item.status === 'Draft').length;

  return (
    <AdminShell
      pageTitle={isManagerView ? 'Manager Employee View' : 'Employee Dashboard'}
      subtitle="Track your expenses, budget use, and reimbursement progress."
      menuItems={isManagerView ? managerMenuItems : employeeMenuItems}
      profileName={isManagerView ? 'Approving Manager' : 'Employee'}
      profileRoleTag={isManagerView ? 'MANAGER' : 'EMPLOYEE'}
    >
      <article className="rounded-[16px] border border-[#cde4ef] bg-gradient-to-r from-[#e9f6fb] via-[#f4fbff] to-[#ffffff] px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-extrabold tracking-[0.1em] text-[#4a7a8f]">CURRENT TRIP CONTEXT</p>
            <h2 className="mt-1 text-[22px] font-extrabold text-[#0b5f77]">
              {activeTrip ? `You are currently on ${activeTrip.destination}` : 'No active trip selected'}
            </h2>
            <p className="mt-1 text-[13px] font-semibold text-[#5f8194]">
              {activeTrip ? `${activeTrip.period} • ${activeTrip.status}` : 'Create/submit a trip to unlock expense budgeting.'}
            </p>
          </div>
          <div className="rounded-[12px] border border-[#d8eaf3] bg-white px-4 py-3 text-right">
            <p className="text-[11px] font-extrabold tracking-[0.09em] text-[#8fa9ba]">BUDGET REMAINING</p>
            <p className={`mt-1 text-[26px] font-extrabold ${remainingBudget < 0 ? 'text-[#b54747]' : 'text-[#0b7f59]'}`}>
              {formatINR(Math.abs(remainingBudget))}
              {remainingBudget < 0 ? ' over' : ''}
            </p>
            <p className="text-[12px] font-semibold text-[#7995a8]">
              {formatINR(activeTrip?.budgetUsed || 0)} used of {formatINR(activeTrip?.budgetCap || 0)}
            </p>
          </div>
        </div>
      </article>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-[-0.03em] text-[#0b5f77] sm:text-[34px]">My Expenses</h1>
          <p className="mt-1 text-[15px] font-semibold text-[#5a8093]">Manage your claims and trip budget in one place.</p>
        </div>
        <Link
          to={isManagerView ? '/manager/expenses/new' : '/employee/expenses/new'}
          className="inline-flex h-[44px] items-center justify-center rounded-full bg-[#0d869f] px-5 text-[14px] font-bold text-white shadow-[0_10px_20px_rgba(13,113,143,0.25)] transition hover:bg-[#0a7790]"
        >
          + Add New Expense
        </Link>
      </div>

      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-[14px] font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading ? (
        <>
          <div className="grid gap-4 xl:grid-cols-[2fr_0.9fr]">
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="rounded-full bg-[#bde9f6] px-3 py-1 text-[11px] font-extrabold tracking-[0.08em] text-[#0f728d]">
                    ACTIVE TRIP
                  </span>
                  <h2 className="mt-3 text-[30px] font-extrabold tracking-[-0.02em] text-[#0b5f77]">
                    {activeTrip?.destination || 'No Active Trip'}
                  </h2>
                  <p className="text-[15px] font-semibold text-[#6e8ba0]">{activeTrip?.period || 'No active dates'}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-extrabold tracking-[0.1em] text-[#90abc0]">BUDGET</p>
                  <p className="text-[24px] font-extrabold text-[#0b708b]">
                    {formatINR(activeTrip?.budgetUsed || 0)}
                  </p>
                  <p className="text-[13px] font-semibold text-[#8ea5b7]">
                    / {formatINR(activeTrip?.budgetCap || 0)}
                  </p>
                </div>
              </div>
              <div className="mt-4 h-3 rounded-full bg-[#deebf2]">
                <div className="h-full rounded-full bg-[#0a7a93]" style={{ width: `${budgetPercent}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[13px] font-bold text-[#6e8ba0]">
                <span>{budgetPercent}% consumed</span>
                <span>{formatINR(Math.abs(remainingBudget))}{remainingBudget < 0 ? ' over' : ' remaining'}</span>
              </div>
            </article>

            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <h3 className="text-[20px] font-extrabold text-[#0b5f77]">Spending Insights</h3>
              <p className="mt-2 text-[24px] font-extrabold text-[#0b5f77]">{formatINR(spendingByCategory.totalAmount)}</p>
              <ul className="mt-3 space-y-2 text-[13px] font-semibold text-[#5f7f93]">
                {spendingByCategory.topThree.map((item) => (
                  <li key={item.name} className="flex items-center justify-between">
                    <span>{item.name}</span>
                    <span>{item.percent}%</span>
                  </li>
                ))}
              </ul>
            </article>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">PAID TOTAL</p>
              <p className="mt-1 text-[24px] font-extrabold text-[#0b5f77]">{formatINR(payerSummary.paid)}</p>
            </article>
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">APPROVED REIMBURSABLE</p>
              <p className="mt-1 text-[24px] font-extrabold text-[#0b7f59]">{formatINR(payerSummary.approvedReimbursable)}</p>
            </article>
            <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
              <p className="text-[11px] font-extrabold tracking-[0.08em] text-[#8ea8bb]">AT RISK</p>
              <p className="mt-1 text-[24px] font-extrabold text-[#b54747]">{formatINR(payerSummary.atRisk)}</p>
            </article>
          </div>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-[22px] font-extrabold text-[#0b5f77]">Expense History</h2>
              <div className="flex flex-wrap gap-2 rounded-full bg-[#edf5fa] p-1">
                {filterTabs.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setFilter(tab)}
                    className={`rounded-full px-4 py-1.5 text-[13px] font-bold ${
                      filter === tab ? 'bg-white text-[#0f6f89]' : 'text-[#7a95a6]'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="text-left text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                    <th className="pb-3">Merchant</th>
                    <th className="pb-3">Category</th>
                    <th className="pb-3">Amount</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExpenses.map((expense) => (
                    <tr key={expense.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                      <td className="py-3">{expense.merchant}</td>
                      <td className="py-3">{expense.category}</td>
                      <td className="py-3 font-extrabold text-[#183344]">{formatINR(expense.amount)}</td>
                      <td className="py-3">
                        <span className={`rounded px-2 py-1 text-[11px] font-extrabold tracking-[0.08em] ${
                          expense.status === 'Approved'
                            ? 'bg-[#bde8f5] text-[#0c6f88]'
                            : expense.status === 'Pending'
                              ? 'bg-[#dbeffd] text-[#2f6e9f]'
                              : expense.status === 'Draft'
                                ? 'bg-[#edf1f5] text-[#7d8fa1]'
                                : 'bg-[#fee7e7] text-[#b35656]'
                        }`}>
                          {expense.status}
                        </span>
                      </td>
                      <td className="py-3">{expense.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex items-center justify-between text-[12px] font-bold tracking-[0.08em] text-[#9bb6c6]">
              <p>APPROVED: {approvedCount} • PENDING: {pendingCount} • DRAFT: {draftCount}</p>
              <span className="text-[#177f9a]">LIVE STATUS</span>
            </div>
          </article>

          <article className="rounded-[16px] border border-[#deebf2] bg-white px-5 py-4">
            <h2 className="text-[22px] font-extrabold text-[#0b5f77]">My Shares Timeline</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="text-left text-[11px] font-extrabold uppercase tracking-[0.08em] text-[#9ab4c4]">
                    <th className="pb-3">Share</th>
                    <th className="pb-3">Trip</th>
                    <th className="pb-3">Category</th>
                    <th className="pb-3">Amount</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  {myShares.map((share) => (
                    <tr key={share.id} className="border-t border-[#ecf4f8] text-[14px] font-semibold text-[#2f4f61]">
                      <td className="py-2.5 font-bold text-[#0f5f78]">{share.id.slice(0, 8)}</td>
                      <td className="py-2.5">{share.tripId.slice(0, 8)}</td>
                      <td className="py-2.5">{share.category}</td>
                      <td className="py-2.5">{formatINR(share.amount)}</td>
                      <td className="py-2.5">
                        <span className={`rounded-full px-2 py-1 text-[11px] font-extrabold tracking-[0.08em] ${
                          share.status === 'Approved'
                            ? 'bg-[#bde8f5] text-[#0c6f88]'
                            : share.status === 'Pending'
                              ? 'bg-[#dbeffd] text-[#2f6e9f]'
                              : share.status === 'Draft'
                                ? 'bg-[#edf1f5] text-[#7d8fa1]'
                                : 'bg-[#fee7e7] text-[#b35656]'
                        }`}>
                          {share.status}
                        </span>
                      </td>
                      <td className="py-2.5 text-[13px]">{share.timeline}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>
        </>
      ) : null}
    </AdminShell>
  );
};

export default EmployeeDashboardPage;
